/* eslint-disable @typescript-eslint/no-explicit-any */
import {CommonModule} from '@angular/common';
import type {AfterViewInit, ElementRef, OnDestroy} from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import {
  BUTTON_COLOR,
  BUTTON_VARIANT,
  FvButtonComponent,
  FvContextMenuComponent,
  FvModalComponent,
  FvSpinnerComponent,
  IFvContextMenuOption,
} from '@discocil/fv-components';
import {
  BehaviorSubject, catchError, EMPTY,
  filter,
  finalize,
  first,
  from,
  fromEvent,
  map,
  merge,
  Observable,
  of,
  switchMap,
  tap
} from 'rxjs';

import {FV_SCANNER_STATUS} from './fv-scanner-lite.enum';
import {ZXingScannerComponent, ZXingScannerModule} from '@zxing/ngx-scanner';
import {backCameraPattern} from './config/allowed-cameras.config';
import {allowedFormats} from './config/allowed-formats.config';
import {ISafeAreaInsets} from './enum/safe-area-insets.interface';
import {SELECTED_CAMERA_STORAGE_KEY, VIDEO_POSTER,} from './config/fv-scanner-lite.config';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {Logger} from 'src/app/core/interfaces/Logger';

@Component({
  selector: 'fv-scanner',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FvSpinnerComponent,
    FvButtonComponent,
    FvContextMenuComponent,
    ZXingScannerModule,
  ],
  templateUrl: './fv-scanner-lite.component.html',
  styleUrl: './fv-scanner-lite.component.scss',
})
export class FvScannerLiteComponent implements OnDestroy, AfterViewInit {
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly logger = inject(Logger);

  @ViewChild('scanner') scanner!: ZXingScannerComponent;
  @ViewChild('cameraSelectorContextMenu')
  cameraSelectorContextMenu!: FvContextMenuComponent;
  @ViewChild('modal') modal!: FvModalComponent;
  @ViewChild('scannerExtraContent') scannerExtraContent!: ElementRef;
  @ViewChild('video') video!: HTMLVideoElement;

  @Input() defaultCameraName = 'Default camera';
  @Output() scanSuccess: EventEmitter<string> = new EventEmitter<string>();
  @Output() scanError: EventEmitter<Error> = new EventEmitter<Error>();
  @Output() closeEvent: EventEmitter<void> = new EventEmitter<void>();

  isSuccessAlertVisible = false;
  isSuccessFadeOutActive = false;

  BUTTON_COLOR = BUTTON_COLOR;
  BUTTON_VARIANT = BUTTON_VARIANT;
  FV_SCANNER_STATUS = FV_SCANNER_STATUS;
  scannerStatus = FV_SCANNER_STATUS.loading;
  showScanner = false;
  isScannerStarted = false;
  isScannerModalWrapperOpen = false;

  allowedFormats = allowedFormats;
  devices: MediaDeviceInfo[] = [];
  hasPermissions = false;
  lastDecodedTime = 0;
  lastDecodedText = '';

  private isFlashAvailable$ = new BehaviorSubject<boolean>(false);
  isFlashEnabled = false;
  isCloseEnabled = false;

  get flashIcon(): string {
    return this.isFlashEnabled ? 'far fa-bolt' : 'far fa-bolt-slash';
  }

  get hasExtraContent(): boolean {
    return !!this.scannerExtraContent?.nativeElement?.children?.length;
  }

  get insetTop(): string {
    return this.getSafeAreaInset().top + 'px';
  }

  get isFlashAvailable(): boolean {
    return this.isFlashAvailable$.getValue();
  }

  get showCameraSelector(): boolean {
    return this.devices.length > 1;
  }

  private isViewInitialized = false;

  ngAfterViewInit(): void {
    this.isViewInitialized = true;
    this.changeDetectorRef.markForCheck();
  }


  onClickFlash(): void {
    this.toggleFlash();
  }

  private cleanupScannerState(): void {
    this.lastDecodedText = '';
    this.lastDecodedTime = 0;
    this.isFlashEnabled = false;
    this.isFlashAvailable$.next(false);
    this.hasPermissions = false;
    this.devices = [];
  }

  open(): void {
    this.cleanupScannerState();
    this.showScanner = true;
    this.isScannerModalWrapperOpen = true;
    this.scannerStatus = FV_SCANNER_STATUS.loading;

    if (this.isViewInitialized && this.scanner) {
      this.setupScanner();
    } else {
      setTimeout(() => this.setupScanner(), 0);
    }
  }

  private setupScanner(): void {
    if (this.scanner) {
      const video: HTMLVideoElement = this.scanner.previewElemRef.nativeElement;

      video.setAttribute('playsinline', 'true');
      video.poster = VIDEO_POSTER;

      this.scanner.videoConstraints = {
        aspectRatio: 1,
        frameRate: 10,
        facingMode: {exact: 'environment'},
        advanced: [
          {
            facingMode: {
              exact: 'environment',
            },
          },
        ],
      };
      this.scanner.poster = '';
      this.enableScanner();
      this.initializeCamera();
    }

    this.changeDetectorRef.markForCheck();
  }

  onClickClose(): void {
    this.scannerStatus = FV_SCANNER_STATUS.stopped;

    if (this.isFlashAvailable && this.isFlashEnabled) {
      this.onClickFlash();
    }

    this.close();
    this.isCloseEnabled = false;
    this.changeDetectorRef.markForCheck();
  }

  protected onScanSuccess(result: string): void {
    const now = Date.now();

    if (this.lastDecodedTime && now - this.lastDecodedTime > 10000) {
      this.restartScanner();

      return;
    }

    if (result === this.lastDecodedText && now - this.lastDecodedTime < 2000) {
      return;
    }

    this.lastDecodedText = result;
    this.lastDecodedTime = now;
    this.scanSuccess.next(result);
    this.scanSuccess = new EventEmitter<string>();
  }

  protected onScanError(result: Error): void {
    if (
      result.message?.includes('NotReadableError') ||
      result.message?.includes('NotFoundError')
    ) {
      this.restartScanner();
    }

    this.scanError.next(result);
  }

  protected onFlashCompatible(isCompatible: boolean): void {
    this.isFlashAvailable$.next(!!isCompatible);
  }

  private getCameras(): Observable<MediaDeviceInfo[]> {
    return from(this.scanner.updateVideoInputDevices()).pipe(first());
  }

  private getPermissions(): Observable<boolean> {
    return from(this.scanner.askForPermission()).pipe(first());
  }

  private enableScanner(): void {
    this.scanner.enable = true;
  }

  private disableScanner(): void {
    this.scanner.enable = false;
  }

  private selectDevice(device: MediaDeviceInfo): void {
    this.scanner.device = device;

    // Save selected camera to localStorage
    localStorage.setItem(SELECTED_CAMERA_STORAGE_KEY, device.deviceId);
  }

  private toggleFlash(): void {
    if (this.isFlashAvailable) {
      this.isFlashEnabled = !this.isFlashEnabled;
      this.scanner.torch = this.isFlashEnabled;
    }
  }

  /**
   * If stop fails, we will launch another empty try after
   * open finishes, if it's starting.
   * This bug is common on the library.
   *
   * @returns Promise
   */
  private close(): void {
    this.showScanner = false;

    if (this.scanner) {
      this.scanner.reset();

      if (this.scanner.device) {
        this.scanner.device = undefined;
      }

      this.scanner.enable = false;
    }

    this.isScannerModalWrapperOpen = false;
    this.isScannerStarted = false;
    this.lastDecodedText = '';
    this.lastDecodedTime = 0;
    this.isFlashEnabled = false;
    this.isFlashAvailable$.next(false);
    this.devices = [];
    this.closeEvent.emit();
    this.scanSuccess = new EventEmitter<string>();
    this.changeDetectorRef.markForCheck();
  }

  protected getSafeAreaInset(): ISafeAreaInsets {
    return {
      top: parseInt(
        getComputedStyle(document.documentElement).getPropertyValue(
          '--safe-area-inset-top'
        )
      ),
      bottom: parseInt(
        getComputedStyle(document.documentElement).getPropertyValue(
          '--safe-area-inset-bottom'
        )
      ),
      left: parseInt(
        getComputedStyle(document.documentElement).getPropertyValue(
          '--safe-area-inset-left'
        )
      ),
      right: parseInt(
        getComputedStyle(document.documentElement).getPropertyValue(
          '--safe-area-inset-right'
        )
      ),
    };
  }

  protected onClickCameraSelection(event: MouseEvent): void {
    if (!this.devices?.length) {
      return;
    }

    const options: IFvContextMenuOption[] = this.devices.map(
      (device: MediaDeviceInfo) => {
        let icon = 'far fa-video';
        let label = device.label || this.defaultCameraName;

        if (
          device.label.toLowerCase().includes('front') ||
          device.label.toLowerCase().includes('facetime')
        ) {
          icon = 'far fa-webcam';
          label = 'Front Camera';
        }

        return {
          icon,
          title: label,
          handler: () => this.selectDevice(device),
        };
      }
    );

    this.cameraSelectorContextMenu.openContextMenu(event, options, {
      isRight: true,
    });
  }

  private async restartScanner(): Promise<void> {
    this.disableScanner();
    this.scanner.reset();
    this.scanner.device = undefined;
    await new Promise((resolve) => setTimeout(resolve, 500));
    this.enableScanner();
    this.initializeCamera();
  }

  private resolveSelectedCamera(devices: MediaDeviceInfo[]): MediaDeviceInfo {
    const savedId = localStorage.getItem(SELECTED_CAMERA_STORAGE_KEY);

    return (
      devices.find(d => d.deviceId === savedId) ??
      devices.find(d => backCameraPattern.test((d.label ?? '').toLowerCase())) ??
      devices[0]
    );
  }

  private hasSufficientVideoData(video?: HTMLVideoElement | null): boolean {
    return !!video && video.readyState >= HTMLMediaElement.HAVE_ENOUGH_DATA;
  }

  private getMediaStream(video?: HTMLVideoElement | null): MediaStream | undefined {
    return video?.srcObject instanceof MediaStream ? video.srcObject : undefined;
  }

  private hasLiveTrack(stream?: MediaStream): boolean {
    return !!stream?.getVideoTracks().some(t => t.readyState === 'live');
  }

  private isPreviewReady(device: MediaDeviceInfo | undefined, video?: HTMLVideoElement | null): boolean {
    const stream = this.getMediaStream(video);

    return !!device && this.hasSufficientVideoData(video) && this.hasLiveTrack(stream);
  }

  private waitForVideoReady$(): Observable<void> {
    const video = this.scanner?.previewElemRef?.nativeElement as HTMLVideoElement | undefined;
    const device = this.scanner?.device;

    if (!video || !device) {
      return of(void 0);
    }

    if (this.isPreviewReady(device, video)) {
      return of(void 0);
    }

    return merge(
      fromEvent(video, 'loadeddata'),
      fromEvent(video, 'canplay'),
      fromEvent(video, 'playing')
    ).pipe(
      filter(() => this.isPreviewReady(device, video)),
      first(),
      map(() => void 0)
    );
  }

  private initializeCamera(): void {
    this.getPermissions()
      .pipe(
        tap(permissions => (this.hasPermissions = permissions)),
        switchMap(() => this.getCameras()),
        tap(devices => {
          this.devices = devices;
          this.scannerStatus = FV_SCANNER_STATUS.scanning;
        }),
        filter(devices => devices?.length > 0),
        map(devices => this.resolveSelectedCamera(devices)),
        tap(device => this.selectDevice(device)),
        switchMap(() => this.waitForVideoReady$()),
        tap(() => {
          this.isScannerStarted = true;
          this.isCloseEnabled = true;
        }),
        finalize(() => this.changeDetectorRef.markForCheck()),
        catchError(() => {
          this.scannerStatus = FV_SCANNER_STATUS.stopped;
          this.changeDetectorRef.markForCheck();

          return EMPTY;
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.logger.debug('[FvScannerLiteComponent] Cleaning up resources');

    if (this.isScannerModalWrapperOpen) {
      this.onClickClose();
    }

    this.isFlashAvailable$.complete();
    this.scanSuccess.complete();
    this.scanError.complete();
    this.closeEvent.complete();
  }
}
